// Rug Room Shots: "New job" tab. Two modes:
//   * no job in the URL: the input form (paste or upload + options) and the Plan button;
//   * ?job=<id>: the plan view for that job (summary, estimate, plan table with
//     style / room / exclude overrides, the speed chooser - Fast or Half price - and the
//     Start button). Also used as the read-mode "Open plan" from the Jobs tab.
// Contract: rug-room-shots-worker/SPEC.md sections 3.4, 5.3 and 9.11.
// 2026-10-21 build spec 4.4-4.11 / 9.5: oval and octagon inputs make nothing (Alert); typed ItemIDs make only their own
// shape; the existing-photo check and its source; the Outdoor & sunroom summary and "Sunroom shot" rows; room types
// (letter names, rule labels, gaps with reasons); a manual room pick asks before it un-skips a rug that already has a
// room photo (409 confirm_existing) and warns before an unusual room; the style menu never offers Sunroom;
// "Re-plan with the current rules"; plans made before this release ask before Start (409 old_plan);
// "Download images..." once the job has started.
// Review fixes 2026-10-21: re-plan is offered only once the worker plans with the room-type rules
// (config.features.roomRules); a room pick on a row the worker is re-planning is refused (409 replanning) and the
// table reloads; a live room check cut short says how many WebIDs were checked against the snapshot only.
import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import {
  Box, Paper, Typography, Stack, TextField, Button, Checkbox, FormControlLabel, FormGroup, Switch,
  Chip, Alert, LinearProgress, CircularProgress, Table, TableHead, TableBody, TableRow, TableCell,
  TableContainer, TablePagination, MenuItem, Tooltip, Popover, Dialog, DialogTitle, DialogContent,
  DialogActions, ToggleButtonGroup, ToggleButton, List, ListItem, ListItemText, Divider,
} from '@mui/material';
import { useTheme } from '@mui/material/styles';
import {
  MdContentPaste, MdUploadFile, MdFileDownload, MdPlayArrow, MdRefresh, MdAdd, MdCancel, MdRateReview, MdDownload,
  MdAutorenew,
} from 'react-icons/md';
import * as XLSX from 'xlsx';
import rrsApi from './api';
import RoomPicker from './RoomPicker';
import DownloadDialog from './DownloadDialog';
import {
  AI_COLOR, primaryButtonSx, PLAN_STATUS, GEN_STATUS, JOB_STATUS, REVIEW_STATE,
  fmtUsd, fmtRate, fmtInt, methodWords, styleName, shapeName, easternFileStamp, errText,
  usePolling, useNow, Tile, StyleChip, StatusChip, SiloKindBadge, FlagIcons, NyTime, ImageLightbox, ConfirmDialog,
  styleColor, isDailyLimit, dailyLimitText, overBudget, overBudgetText, signalSpend,
  manualStyles, conflictCode, serverText, WARN_CODES, SunroomShotChip,
} from './common';
import {
  RULE_LABELS, roomLetterOf, letterName, describeLetters, roomPickWarnings,
} from './roomRules';
import {
  SpeedChooser, SpeedChip, BatchesSection, speedsFromConfig, rateFor, estimateFor, defaultSpeed, speedLabel,
  bothTotalsText, isBatch, batchUnavailableFromError,
} from './speed';

const MAX_UPLOAD_BYTES = 5 * 1024 * 1024;
const OUTLINE_KEYS = ['Oval', 'Octagon'];
const OLD_PLAN_TEXT = 'This plan was made before the room-type rules, the outdoor & sunroom list, the new existing-photo check and the oval rule.';
const OLD_PLAN_START_TEXT = 'This plan was made before the room-type rules, the outdoor & sunroom list, the new existing-photo check and the oval rule. Re-plan it with the current rules, or start it anyway.';
const REPLAN_NOT_READY_TEXT = 'Re-plan becomes available when the worker is updated for the room-type rules.';
// A plan made before the 2026-10-21 release: planned, never started, and its summary has no roomRules key (spec 4.11).
const isOldPlan = (job) => !!job && job.status === 'planned' && !job.startedAt
  && !(job.summary && typeof job.summary === 'object' && Object.prototype.hasOwnProperty.call(job.summary, 'roomRules'));
const sumValues = (o) => Object.values(o || {}).reduce((a, n) => a + (Number(n) || 0), 0);
const NOT_MADE_WORDS = {
  no_oval_room: 'oval size: no oval room exists yet',
  no_octagon_room: 'octagon size: no octagon room exists yet',
  unknown_shape: 'a catalog shape this tool does not make',
};
const listIds = (list, n) => {
  const shown = list.slice(0, n);
  return `${shown.join(', ')}${list.length > n ? `, and ${fmtInt(list.length - n)} more` : ''}`;
};
const HEADER_WORDS = new Set(['ITEMID', 'ITEM ID', 'WEBID', 'WEB ID', 'SKU', 'ITEM SKU']);

// Same normalisation as the backend (SPEC 3.2) - used only for the live
// "N IDs detected" hint; the backend is authoritative.
const normaliseIds = (text) => {
  const seen = new Set();
  let invalid = 0;
  String(text || '').split(/[\n\r,;\t ]+/).forEach((raw) => {
    const v = raw.trim().replace(/^\d+[.)]\s*/, '').replace(/\.(jpe?g|png)$/i, '').toUpperCase();
    if (!v || HEADER_WORDS.has(v)) return;
    if (!/^[A-Z0-9][A-Z0-9-]{2,63}$/.test(v)) { invalid += 1; return; }
    seen.add(v);
  });
  return { count: seen.size, invalid };
};

const blockerText = (b) => {
  if (!b) return '';
  if (b === 'planning') return 'The plan is still being worked out.';
  if (b === 'nothing_ready') return 'Nothing is ready to generate.';
  if (b === 'cancelled') return 'This job was cancelled.';
  if (b === 'plan_failed') return 'Planning failed; create a new job to retry.';
  if (b.startsWith('replanning:')) return `${b.split(':')[1]} target(s) are being re-planned.`;
  return b.replace(/_/g, ' ');
};

// ===========================================================================
// Input form
// ===========================================================================
const NewJobForm = ({ config, onOpenJob, notify, health }) => {
  const theme = useTheme();
  const shapes = config.shapes;
  const speeds = useMemo(() => speedsFromConfig(config, health), [config, health]);
  const [mode, setMode] = useState('paste');
  const [name, setName] = useState('');
  const [text, setText] = useState('');
  const [file, setFile] = useState(null);
  const [dragOver, setDragOver] = useState(false);
  const [selShapes, setSelShapes] = useState(() => shapes.map((s) => s.code));
  const [allowSibling, setAllowSibling] = useState(true);
  const [includeExisting, setIncludeExisting] = useState(false);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState('');
  const fileRef = useRef(null);

  const hint = useMemo(() => normaliseIds(text), [text]);

  const acceptFile = (f) => {
    setError('');
    if (!f) return;
    if (!/\.(csv|txt|xlsx|xls)$/i.test(f.name)) { setError('Choose a .csv, .txt, .xlsx or .xls file.'); return; }
    const maxBytes = config.maxUploadBytes || MAX_UPLOAD_BYTES;
    if (f.size > maxBytes) { setError(`The file is over ${Math.round(maxBytes / 1048576)} MB.`); return; }
    setFile(f);
  };

  const downloadTemplate = () => {
    const ws = XLSX.utils.aoa_to_sheet([['WebID']]);
    ws['!cols'] = [{ wch: 18 }];
    const wb = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(wb, ws, 'WebIDs');
    XLSX.writeFile(wb, `Rug Room Shots Template ${easternFileStamp()}.xlsx`);
  };

  const toggleShape = (code) => setSelShapes((cur) => (cur.includes(code) ? cur.filter((c) => c !== code) : [...cur, code]));

  const canSubmit = !busy && selShapes.length > 0 && (mode === 'paste' ? hint.count > 0 : !!file);

  const submit = async () => {
    setBusy(true);
    setError('');
    const options = {
      shapes: shapes.map((s) => s.code).filter((c) => selShapes.includes(c)),
      allowSibling,
      includeExisting,
    };
    try {
      const res = mode === 'paste'
        ? await rrsApi.createJob({ name: name.trim(), text, options })
        : await rrsApi.uploadJob({ file, name: name.trim(), options });
      const rej = (res.rejected || []).length;
      notify(`Job #${res.jobId} created with ${fmtInt(res.inputCount)} ID(s)${rej ? `; ${rej} input(s) rejected` : ''}. Planning...`, rej ? 'warning' : 'success');
      onOpenJob(res.jobId);
    } catch (e) {
      const details = e.body && e.body.details;
      setError(`${errText(e)}${details && typeof details === 'string' ? ` ${details}` : ''}`);
    } finally {
      setBusy(false);
    }
  };

  return (
    <Paper variant="outlined" sx={{ p: { xs: 2, md: 3 }, borderRadius: 2 }}>
      <Stack direction={{ xs: 'column', md: 'row' }} spacing={3}>
        <Box sx={{ flex: 1, minWidth: 0 }}>
          <TextField
            label="Job name (optional)" size="small" fullWidth value={name}
            onChange={(e) => setName(e.target.value.slice(0, 200))}
            placeholder={mode === 'upload' && file ? file.name.replace(/\.[^.]+$/, '') : 'e.g. Fall rugs 10-07'}
            sx={{ mb: 2 }}
          />
          <ToggleButtonGroup size="small" exclusive value={mode} onChange={(e, v) => v && setMode(v)} sx={{ mb: 1.5 }}>
            <ToggleButton value="paste"><MdContentPaste style={{ marginRight: 6 }} /> Paste IDs</ToggleButton>
            <ToggleButton value="upload"><MdUploadFile style={{ marginRight: 6 }} /> Upload file</ToggleButton>
          </ToggleButtonGroup>

          {mode === 'paste' ? (
            <>
              <TextField
                multiline minRows={8} maxRows={18} fullWidth value={text}
                onChange={(e) => setText(e.target.value)}
                placeholder={'WebIDs or ItemIDs, one per line, commas OK\nMAD600C\nCAP202A, NF143C'}
                inputProps={{ spellCheck: false, style: { fontFamily: 'monospace' } }}
              />
              <Typography variant="caption" color="text.secondary">
                {hint.count ? `${fmtInt(hint.count)} unique ID(s) detected` : 'No IDs yet'}
                {hint.invalid ? ` · ${hint.invalid} value(s) do not look like IDs and will be rejected` : ''}
                {hint.count > config.maxIdsPerJob ? ` · over the ${fmtInt(config.maxIdsPerJob)} per-job limit` : ''}
              </Typography>
            </>
          ) : (
            <>
              <Paper
                variant="outlined"
                onDragOver={(e) => { e.preventDefault(); setDragOver(true); }}
                onDragLeave={() => setDragOver(false)}
                onDrop={(e) => { e.preventDefault(); setDragOver(false); acceptFile(e.dataTransfer.files[0]); }}
                onClick={() => fileRef.current && fileRef.current.click()}
                sx={{
                  p: 3, textAlign: 'center', cursor: 'pointer', borderStyle: 'dashed', borderWidth: 2, borderRadius: 2,
                  borderColor: dragOver ? AI_COLOR : 'divider', bgcolor: dragOver ? `${AI_COLOR}10` : 'transparent',
                }}
              >
                <input
                  ref={fileRef} type="file" hidden accept=".csv,.txt,.xlsx,.xls"
                  onChange={(e) => { acceptFile(e.target.files[0]); e.target.value = ''; }}
                />
                <MdUploadFile size={36} style={{ opacity: 0.6 }} />
                <Typography sx={{ mt: 1 }}>
                  {file ? <strong>{file.name}</strong> : 'Drop a CSV, XLSX or TXT here, or click to choose one'}
                </Typography>
                <Typography variant="caption" color="text.secondary">
                  The first column is read. Header words like WebID or ItemID are ignored. Up to 5 MB.
                </Typography>
              </Paper>
              <Button size="small" startIcon={<MdFileDownload />} onClick={downloadTemplate} sx={{ mt: 1 }}>
                Download Template
              </Button>
            </>
          )}
        </Box>

        <Box sx={{ width: { xs: '100%', md: 340 }, flex: '0 0 auto' }}>
          <Typography variant="subtitle2" sx={{ fontWeight: 700, mb: 0.5 }}>Shapes</Typography>
          <FormGroup>
            {shapes.map((s) => (
              <FormControlLabel
                key={s.code}
                control={<Checkbox size="small" checked={selShapes.includes(s.code)} onChange={() => toggleShape(s.code)} />}
                label={<Typography variant="body2">{s.name} <Typography component="span" variant="caption" color="text.secondary">({s.code})</Typography></Typography>}
                sx={{ my: -0.5 }}
              />
            ))}
          </FormGroup>
          <Typography variant="caption" color="text.secondary" display="block" sx={{ mb: 1.5 }}>
            Oval and octagon rugs are never made: no oval room exists yet. An ItemID makes only its own shape (that size
            when it has a photo); a WebID makes every shape ticked here.
          </Typography>
          <Divider sx={{ mb: 1.5 }} />
          <FormControlLabel
            control={<Switch checked={allowSibling} onChange={(e) => setAllowSibling(e.target.checked)} />}
            label={<Typography variant="body2">Allow borrowed same-shape silos</Typography>}
          />
          <FormControlLabel
            control={<Switch checked={includeExisting} onChange={(e) => setIncludeExisting(e.target.checked)} />}
            label={<Typography variant="body2">Also make shots for rugs that already have a room photo of that size or shape on the image server</Typography>}
            sx={{ mt: 1, alignItems: 'flex-start', '& .MuiSwitch-root': { mt: -0.75 } }}
          />
          <Paper variant="outlined" sx={{ mt: 2, p: 1.5, bgcolor: theme.palette.action.hover, borderRadius: 1.5 }}>
            <Typography variant="caption" color="text.secondary" display="block">Generation settings (fixed)</Typography>
            <Stack direction="row" spacing={0.75} sx={{ mt: 0.75, flexWrap: 'wrap', rowGap: 0.75 }}>
              <Chip size="small" label={config.model} />
              <Chip size="small" label={config.imageSize} />
              <Chip size="small" label={config.aspectRatio} />
            </Stack>
            <Typography variant="caption" color="text.secondary" display="block" sx={{ mt: 1 }}>Speed (chosen at Start)</Typography>
            {speeds.map((sp) => (
              <Typography key={sp.code} variant="caption" display="block" color={sp.available ? 'text.primary' : 'text.disabled'}>
                <strong>{sp.label}</strong> · {fmtRate(sp.usdPerImage)} / image · {sp.available ? sp.note : sp.reason}
              </Typography>
            ))}
          </Paper>
        </Box>
      </Stack>

      {error && <Alert severity="error" sx={{ mt: 2 }}>{error}</Alert>}
      <Stack direction="row" spacing={2} alignItems="center" sx={{ mt: 2.5 }}>
        <Button variant="contained" size="large" onClick={submit} disabled={!canSubmit} sx={{ ...primaryButtonSx, minWidth: 160 }}>
          {busy ? <CircularProgress size={22} color="inherit" /> : 'Plan'}
        </Button>
        <Typography variant="body2" color="text.secondary">
          Planning is free except for a fraction of a cent per new silo embedding. Nothing is generated until you click Start.
        </Typography>
      </Stack>
    </Paper>
  );
};

// ===========================================================================
// Plan view
// ===========================================================================
const PAGE_SIZE = 50;

const PlanView = ({ jobId, config, notify, onGoReview, onNewJob, onOpenJob, health }) => {
  const theme = useTheme();
  const [job, setJob] = useState(null);
  const [jobError, setJobError] = useState('');
  const [targets, setTargets] = useState({ total: 0, items: [] });
  const [targetsLoading, setTargetsLoading] = useState(false);
  const [targetsError, setTargetsError] = useState('');
  const [page, setPage] = useState(0);
  const [planStatus, setPlanStatus] = useState('');
  const [styleFilter, setStyleFilter] = useState('');
  const [shapeFilter, setShapeFilter] = useState('');
  const [qInput, setQInput] = useState('');
  const [q, setQ] = useState('');
  const [busyTarget, setBusyTarget] = useState(null);

  const [styleMenu, setStyleMenu] = useState(null); // { anchor, target }
  const [styleNote, setStyleNote] = useState('');
  const [roomDialog, setRoomDialog] = useState(null); // target
  const [roomChoice, setRoomChoice] = useState('');
  const [roomNote, setRoomNote] = useState('');
  const [roomBusy, setRoomBusy] = useState(false);
  const [roomChoiceObj, setRoomChoiceObj] = useState(null); // the chosen room (letter / base shape warnings)
  // Asked before a room pick is (re)sent: { kind: 'unusual' | 'existing', text } (spec 4.5 / 4.7).
  const [roomAsk, setRoomAsk] = useState(null);
  const [roomPreview, setRoomPreview] = useState(null);
  const [lightbox, setLightbox] = useState(null);
  const [listDialog, setListDialog] = useState(null); // 'unresolved' | 'rejected' | 'notMade'
  const [oldPlanOpen, setOldPlanOpen] = useState(false); // spec 4.11
  const [oldPlanInfo, setOldPlanInfo] = useState(null); // 409 old_plan "current"
  const [replanOpen, setReplanOpen] = useState(false); // spec 4.10
  const [replanNote, setReplanNote] = useState('');
  const [replanBusy, setReplanBusy] = useState(false);
  const [downloadOpen, setDownloadOpen] = useState(false);
  const [startOpen, setStartOpen] = useState(false);
  const [startBusy, setStartBusy] = useState(false);
  const [planChanged, setPlanChanged] = useState('');
  const [cancelOpen, setCancelOpen] = useState(false);
  const [cancelNote, setCancelNote] = useState('');
  const [cancelBusy, setCancelBusy] = useState(false);
  const [exporting, setExporting] = useState(false);
  const speeds = useMemo(() => speedsFromConfig(config, health), [config, health]);
  const [speed, setSpeed] = useState(null); // null until the job's own speed is known
  const [batchBlocked, setBatchBlocked] = useState('');
  const [limitMsg, setLimitMsg] = useState(''); // 409 daily_limit (non-admins): the server's words

  const targetSeq = useRef(0);

  const loadJob = useCallback(async () => {
    try {
      const j = await rrsApi.getJob(jobId);
      setJob(j);
      setJobError('');
      return j;
    } catch (e) {
      setJobError(errText(e));
      return null;
    }
  }, [jobId]);

  const loadTargets = useCallback(async () => {
    const seq = targetSeq.current + 1;
    targetSeq.current = seq;
    setTargetsLoading(true);
    try {
      const res = await rrsApi.getTargets(jobId, {
        planStatus, style: styleFilter, shape: shapeFilter, q, page: page + 1, pageSize: PAGE_SIZE,
      });
      if (seq === targetSeq.current) {
        setTargets({ total: res.total || 0, items: res.items || [] });
        setTargetsError('');
      }
    } catch (e) {
      if (seq === targetSeq.current) setTargetsError(errText(e));
    } finally {
      if (seq === targetSeq.current) setTargetsLoading(false);
    }
  }, [jobId, planStatus, styleFilter, shapeFilter, q, page]);

  const refreshAll = useCallback(async () => {
    const j = await loadJob();
    if (j && j.status !== 'planning') await loadTargets();
  }, [loadJob, loadTargets]);

  // Only the job's status (not every poll of the job) re-triggers the target fetch.
  const jobStatus = job ? job.status : null;
  useEffect(() => { loadJob(); }, [loadJob]);
  useEffect(() => {
    if (jobStatus && jobStatus !== 'planning') loadTargets();
  }, [loadTargets, jobStatus]);

  // Debounce the search box.
  useEffect(() => {
    const t = setTimeout(() => { setPage(0); setQ(qInput.trim().toUpperCase()); }, 400);
    return () => clearTimeout(t);
  }, [qInput]);

  // Preselect the job's speed (the speed of its latest Start) once, when it is still offered.
  const jobSpeedValue = job ? job.speed : null;
  useEffect(() => {
    if (speed !== null || !job) return;
    setSpeed(defaultSpeed(jobSpeedValue, speeds));
  }, [speed, job, jobSpeedValue, speeds]);
  // If Half price stops being available, the choice is KEPT (a money choice is never switched
  // silently, e.g. by one stale health poll during a worker restart): Start is disabled with
  // the reason until it is available again, or until the user picks Fast themselves.
  const batchOffered = speeds.some((x) => isBatch(x.code) && x.available);
  const chosenSpeed = speed || 'fast';
  const speedBlocked = isBatch(chosenSpeed) && !batchOffered;
  const speedBlockedText = speedBlocked
    ? `${(speeds.find((x) => isBatch(x.code)) || {}).reason || 'Half price is not available right now.'} Choose Fast, or wait until half price is available again.`
    : '';

  const jobReplanning = job && job.targetCounts ? job.targetCounts.replanning || 0 : 0;
  const pageReplanning = targets.items.some((t) => t.planStatus === 'replanning');
  const shouldPoll = !!job && (job.status === 'planning' || jobReplanning > 0 || pageReplanning);
  usePolling(refreshAll, 2000, shouldPoll);
  // Half-price batches of this job: their state is re-read every minute while any is active.
  const jobBatches = (job && Array.isArray(job.batches)) ? job.batches : [];
  const batchesActive = jobBatches.some((b) => !b.finishedAt && !['done', 'create_failed', 'cancelled_unsent', 'unknown'].includes(b.status));
  usePolling(loadJob, 60000, batchesActive && !shouldPoll);
  const now = useNow(batchesActive, 30000);

  const styles = config.styles;
  const smallMax = config.roomRules && config.roomRules.effective ? config.roomRules.effective.round_small_max : undefined;
  const canEditJob = job && !['cancelled', 'plan_failed', 'planning'].includes(job.status);
  const replaceTarget = (updated) => setTargets((cur) => ({
    ...cur, items: cur.items.map((t) => (t.targetId === updated.targetId ? { ...t, ...updated } : t)),
  }));

  // ---- overrides --------------------------------------------------------
  const applyStyle = async (styleNameValue) => {
    const t = styleMenu && styleMenu.target;
    if (!t) return;
    setStyleMenu(null);
    setBusyTarget(t.targetId);
    try {
      const res = await rrsApi.setStyle(t.targetId, { style: styleNameValue, note: styleNote.trim(), jobId });
      const code = (styles.find((s) => s.name === styleNameValue) || {}).code;
      replaceTarget({
        targetId: t.targetId,
        planStatus: res.planStatus || t.planStatus,
        style: { ...(t.style || {}), code, name: styleNameValue, method: 'override', confidence: null, runnerUp: t.style ? t.style.name : null },
      });
      const others = res.otherSizesUpdated || 0;
      const kept = res.otherSizesAlreadyGenerated || 0;
      notify(
        `${t.webId}: style set to ${styleNameValue}`
        + (others ? ` on ${others + 1} sizes` : '')
        + (res.planStatus === 'replanning' || others ? '; picking rooms...' : '')
        + (kept ? `. ${kept} size${kept === 1 ? ' was' : 's were'} already generated and keep${kept === 1 ? 's' : ''} the old style - regenerate to apply.` : ''),
        kept ? 'warning' : 'success');
      await loadJob();
    } catch (e) {
      // 409 scene_list_only (Sunroom by hand) / twin_style_fixed (a sunroom shot): the server's words.
      const code = conflictCode(e);
      notify(code && WARN_CODES.includes(code) ? serverText(e) : errText(e), code ? 'warning' : 'error');
    } finally {
      setBusyTarget(null);
      setStyleNote('');
    }
  };

  const openRoomDialog = (t) => {
    setRoomDialog(t);
    setRoomChoice((t.room && t.room.code) || '');
    setRoomChoiceObj(null);
    setRoomAsk(null);
    setRoomNote('');
  };

  // Sends the room pick. A rug that already has a room photo is refused with 409 confirm_existing until the
  // person confirms (then resent with confirmExisting); 409 scene_room_style shows the server's words.
  const sendRoom = async (confirmExisting) => {
    const t = roomDialog;
    if (!t || !roomChoice) return;
    setRoomBusy(true);
    try {
      const res = await rrsApi.setRoom(t.targetId, { roomCode: roomChoice, note: roomNote.trim(), jobId, confirmExisting });
      if (res && res.targetId) replaceTarget(res);
      notify(`${t.webId} ${t.shapeCode}: room set to ${roomChoice}`, 'success');
      setRoomAsk(null);
      setRoomDialog(null);
      await loadJob();
      if (!res || !res.targetId) await loadTargets();
    } catch (e) {
      const code = conflictCode(e);
      if (code === 'confirm_existing' && !confirmExisting) {
        const cur = (e.body && e.body.current) || {};
        const why = cur.planReason || t.planReason || 'it is on the image server';
        setRoomAsk({ kind: 'existing', text: `This rug already has a room photo on the image server: ${why}. Make another one anyway?` });
      } else if (code === 'replanning') {
        // the worker is re-planning this rug (for example right after it was included again): show that row as it is
        setRoomAsk(null);
        setRoomDialog(null);
        notify(serverText(e), 'warning');
        await loadTargets();
      } else if (code && WARN_CODES.includes(code)) {
        setRoomAsk(null);
        notify(serverText(e), 'warning');
      } else {
        setRoomAsk(null);
        notify(errText(e), 'error');
      }
    } finally {
      setRoomBusy(false);
    }
  };

  // Before saving: warn about a room that is not the usual type for this rug or whose base photo shows an oval rug.
  const applyRoom = () => {
    const t = roomDialog;
    if (!t || !roomChoice) return;
    const room = roomChoiceObj && roomChoiceObj.roomCode === roomChoice ? roomChoiceObj : null;
    const warnings = roomPickWarnings(t, room, smallMax, config.roomLetters);
    if (warnings.length) {
      setRoomAsk({ kind: 'unusual', text: `${warnings.join(' ')} Use it anyway?` });
      return;
    }
    sendRoom(false);
  };

  const toggleExclude = async (t) => {
    const excluded = t.planStatus !== 'excluded';
    setBusyTarget(t.targetId);
    try {
      const res = await rrsApi.setExcluded(t.targetId, excluded);
      replaceTarget({ targetId: t.targetId, planStatus: res.planStatus || (excluded ? 'excluded' : 'replanning') });
      await loadJob();
    } catch (e) {
      notify(errText(e), 'error');
    } finally {
      setBusyTarget(null);
    }
  };

  // ---- start / cancel / export ------------------------------------------
  const est = (job && job.estimate) || { readyCount: 0, usdPerImage: config.usdPerImage, estUsd: 0, embeddingUsdSpent: 0 };
  const isRestart = job && ['running', 'completed'].includes(job.status);
  // The estimate at the chosen speed: the backend recomputes the same number for its
  // stale-plan check (ready x the job's rate at that speed, 4 decimals).
  const chosenRate = rateFor(chosenSpeed, speeds, job);
  const chosenUsd = isBatch(chosenSpeed)
    ? (est.estUsdBatch !== undefined && est.estUsdBatch !== null ? Number(est.estUsdBatch) : estimateFor(est.readyCount, chosenRate))
    : Number(est.estUsd || 0);
  const half = isBatch(chosenSpeed);

  // acknowledgeOldPlan: "Start anyway" for a plan made before the current rules (spec 4.11).
  const doStart = async (acknowledgeOldPlan) => {
    setStartBusy(true);
    setPlanChanged('');
    setBatchBlocked('');
    setLimitMsg('');
    try {
      const res = await rrsApi.startJob(jobId, {
        expectedReadyCount: est.readyCount, expectedUsd: chosenUsd, speed: chosenSpeed, acknowledgeOldPlan: acknowledgeOldPlan === true,
      });
      setStartOpen(false);
      setOldPlanOpen(false);
      signalSpend();
      notify(half
        ? `Queued ${fmtInt(res.queued)} image(s) at half price for about ${fmtUsd(res.estUsd)}. Google usually returns them within hours, at most 24 h. You can close this page.`
        : `Queued ${fmtInt(res.queued)} image(s) for about ${fmtUsd(res.estUsd)}. Results appear as they finish.`, 'success');
      onGoReview(jobId);
    } catch (e) {
      setStartOpen(false);
      const code = e.status === 409 && e.body ? e.body.code : null;
      if (code !== 'old_plan') setOldPlanOpen(false);
      if (code === 'old_plan') {
        // Made before the current rules: re-plan (recommended) or start anyway. Nothing was queued.
        setOldPlanInfo((e.body && e.body.current) || null);
        setOldPlanOpen(true);
      } else if (code === 'plan_changed') {
        // Re-read and re-price at the chosen speed.
        const cur = e.body.current || {};
        const n = cur.readyCount !== undefined ? cur.readyCount : est.readyCount;
        const usd = half ? estimateFor(n, chosenRate) : cur.estUsd;
        setPlanChanged(`The plan changed: now ${fmtInt(n)} image(s), ${fmtUsd(usd)} at ${speedLabel(chosenSpeed)}. Check it and click Start again.`);
      } else if (code === 'batch_unavailable') {
        setBatchBlocked(`${batchUnavailableFromError(e)} Nothing was queued; choose Fast or try later.`);
      } else if (isDailyLimit(e)) {
        // Never retried automatically: show the server's words and refresh the budget.
        setLimitMsg(dailyLimitText(e));
        signalSpend();
      } else {
        notify(errText(e), 'error');
      }
      await refreshAll();
    } finally {
      setStartBusy(false);
    }
  };

  // Re-plan: cancel this planned, never-started job and plan the same IDs again as a new job (spec 4.10).
  const doReplan = async () => {
    setReplanBusy(true);
    try {
      const res = await rrsApi.replanJob(jobId, replanNote.trim());
      notify(
        `Job #${(res && res.oldJobId) || jobId} was cancelled and is being planned again as job #${res && res.newJobId} with the current rules`
        + `${res && res.releasedTargets ? ` (${fmtInt(res.releasedTargets)} unstarted rows released)` : ''}.`,
        'success',
      );
      setReplanOpen(false);
      setOldPlanOpen(false);
      if (res && res.newJobId && onOpenJob) onOpenJob(res.newJobId);
      else await refreshAll();
    } catch (e) {
      const code = conflictCode(e);
      notify(code ? serverText(e) : errText(e), code ? 'warning' : 'error');
    } finally {
      setReplanBusy(false);
    }
  };

  const doCancel = async () => {
    setCancelBusy(true);
    try {
      const res = await rrsApi.cancelJob(jobId, cancelNote.trim());
      notify(`Job cancelled: ${fmtInt(res.cancelledGenerations)} queued generation(s) cancelled, ${fmtInt(res.releasedTargets)} target(s) released.${res.batchesCancelling ? ` ${fmtInt(res.batchesCancelling)} half-price batch(es) at Google are being cancelled; images Google already made are still collected (they were paid for).` : ''}`, 'info');
      setCancelOpen(false);
      await refreshAll();
    } catch (e) {
      notify(errText(e), 'error');
    } finally {
      setCancelBusy(false);
    }
  };

  const doExport = async () => {
    setExporting(true);
    try { await rrsApi.exportJob(jobId); } catch (e) { notify(errText(e), 'error'); } finally { setExporting(false); }
  };

  // ---- render -------------------------------------------------------------
  if (!job) {
    return (
      <Paper variant="outlined" sx={{ p: 3, borderRadius: 2 }}>
        {jobError ? (
          <Stack spacing={2}>
            <Alert severity="error">{jobError}</Alert>
            <Box><Button startIcon={<MdAdd />} onClick={onNewJob}>New job</Button></Box>
          </Stack>
        ) : <Box sx={{ textAlign: 'center', py: 4 }}><CircularProgress /></Box>}
      </Paper>
    );
  }

  const tc = job.targetCounts || {};
  const unresolved = job.unresolved || [];
  const rejected = job.rejected || [];
  const summary = (job.summary && typeof job.summary === 'object') ? job.summary : {};
  const shapesSkipped = summary.shapesSkipped || {};
  // Oval / octagon sizes are never made (spec 4.8); other unmapped catalog shapes are listed apart.
  const outlineSkippedText = OUTLINE_KEYS.filter((k) => shapesSkipped[k]).map((k) => `${k} ${fmtInt(shapesSkipped[k])}`).join(', ');
  const otherSkippedText = Object.entries(shapesSkipped).filter(([k, n]) => n && !OUTLINE_KEYS.includes(k))
    .map(([k, n]) => `${k} ${fmtInt(n)}`).join(', ');
  const labelConflicts = summary.labelConflicts && typeof summary.labelConflicts === 'object' ? summary.labelConflicts : {};
  const labelConflictsCount = Number(summary.labelConflictsCount) || Object.keys(labelConflicts).length;
  // IDs that made nothing: oval / octagon ItemIDs, or a catalog shape this tool does not make.
  const notMade = Array.isArray(summary.inputsNotMade) ? summary.inputsNotMade.filter(Boolean) : [];
  const notMadeCount = Math.max(Number(summary.inputsNotMadeCount) || 0, notMade.length);
  const notMadeOutline = notMade.filter((x) => x.reason === 'no_oval_room' || x.reason === 'no_octagon_room');
  const notMadeUnknown = notMade.filter((x) => x.reason === 'unknown_shape');
  const typed = summary.typedItems && typeof summary.typedItems === 'object' ? summary.typedItems : null;
  const skipCheck = summary.skipCheck && typeof summary.skipCheck === 'object' ? summary.skipCheck : null;
  const scenes = summary.scenes && typeof summary.scenes === 'object' ? summary.scenes : null;
  const byRule = summary.roomRules && summary.roomRules.byRule && typeof summary.roomRules.byRule === 'object' ? summary.roomRules.byRule : {};
  const byRuleText = Object.entries(byRule).filter(([, n]) => Number(n) > 0)
    .map(([k, n]) => `${RULE_LABELS[k] || k} ${fmtInt(n)}`).join(' · ');
  const gapsByReason = summary.gapsByReason && typeof summary.gapsByReason === 'object'
    ? Object.entries(summary.gapsByReason).filter(([, n]) => Number(n) > 0).sort((a, b) => Number(b[1]) - Number(a[1])) : [];
  const sceneText = (() => {
    if (!scenes) return '';
    const parts = [];
    if (Number(scenes.outdoor)) parts.push(`outdoor only ${fmtInt(scenes.outdoor)}`);
    if (Number(scenes.sun)) parts.push(`sunroom only ${fmtInt(scenes.sun)}`);
    const both = Number(scenes.both) ? `Outdoor + sunroom: ${fmtInt(scenes.both)} rug${Number(scenes.both) === 1 ? '' : 's'} (+${fmtInt(scenes.twins || 0)} sunroom shot${Number(scenes.twins) === 1 ? '' : 's'})` : '';
    if (!parts.length && !both) return '';
    return `${both}${both && parts.length ? '; ' : ''}${parts.length ? `on the Outdoor & sunroom list: ${parts.join(', ')}` : ''}.`;
  })();
  const oldPlan = isOldPlan(job);
  let listRows = [];
  if (listDialog === 'unresolved') {
    listRows = unresolved.map((u) => ({ input: typeof u === 'string' ? u : (u.input || u.id || JSON.stringify(u)), reason: u && u.reason }));
  } else if (listDialog === 'notMade') {
    listRows = notMade.map((x) => ({ input: x.input, reason: `${NOT_MADE_WORDS[x.reason] || x.reason || ''}${x.shape ? ` (${x.shape})` : ''}` }));
  } else if (listDialog === 'rejected') {
    listRows = rejected;
  }
  const noGenerations = sumValues(job.genCounts) === 0;
  // Re-planning needs the worker that plans with the room-type rules (else the copy gets the old rules again).
  const replanReady = !!(config.features && config.features.roomRules === true);
  const replanEligible = ['planned', 'plan_failed'].includes(job.status) && !job.startedAt && noGenerations;
  const canReplan = replanEligible && replanReady;

  const summaryChips = [
    { key: 'planned', label: 'Ready', n: est.readyCount, color: 'success', filter: 'planned' },
    { key: 'held', label: 'Held', n: tc.held, color: 'warning', filter: 'held' },
    { key: 'gap', label: 'Gap', n: tc.gap, color: 'error', filter: 'gap' },
    { key: 'skipped_existing', label: 'Already has a room photo', n: tc.skipped_existing, filter: 'skipped_existing' },
    { key: 'existing', label: 'In another job', n: tc.existingElsewhere, color: 'secondary', filter: 'existing_elsewhere' },
    { key: 'excluded', label: 'Excluded', n: tc.excluded, filter: 'excluded' },
    { key: 'replanning', label: 'Re-planning', n: tc.replanning, color: 'info', filter: 'replanning', hideZero: true },
    { key: 'unresolved', label: 'Unresolved IDs', n: unresolved.length || job.unresolvedCount || 0, color: 'warning', dialog: 'unresolved' },
    { key: 'rejected', label: 'Rejected input', n: rejected.length, color: 'error', dialog: 'rejected' },
  ];

  const setFilter = (f) => { setPage(0); setPlanStatus((cur) => (cur === f ? '' : f)); };

  // Group rows by WebID (rows arrive ordered by position, then shape).
  const rows = [];
  let lastWeb = null;
  targets.items.forEach((t) => {
    if (t.webId !== lastWeb) { rows.push({ group: true, webId: t.webId, inputId: t.inputId, design: t.design, key: `g-${t.webId}-${t.targetId}` }); lastWeb = t.webId; }
    rows.push({ group: false, t, key: `t-${t.targetId}` });
  });

  return (
    <Box>
      {/* Header */}
      <Paper variant="outlined" sx={{ p: 2, borderRadius: 2, mb: 2 }}>
        <Stack direction={{ xs: 'column', md: 'row' }} spacing={1.5} alignItems={{ md: 'center' }} justifyContent="space-between">
          <Box sx={{ minWidth: 0 }}>
            <Stack direction="row" spacing={1} alignItems="center" sx={{ flexWrap: 'wrap', rowGap: 0.5 }}>
              <Typography variant="h6" sx={{ fontWeight: 700 }} noWrap>#{job.jobId} · {job.name}</Typography>
              <StatusChip map={JOB_STATUS} status={job.status} />
              {job.startedAt && job.speed ? <SpeedChip speed={job.speed} /> : null}
            </Stack>
            <Typography variant="caption" color="text.secondary">
              {fmtInt(job.inputCount)} input ID(s) · created by {job.createdByEmail || '-'} · <NyTime ts={job.createdAt} variant="caption" />
              {job.lookupBackend ? ` · lookups: ${job.lookupBackend}` : ''}
            </Typography>
          </Box>
          <Stack direction="row" spacing={1} sx={{ flexWrap: 'wrap', rowGap: 1 }}>
            <Button size="small" startIcon={<MdRefresh />} onClick={refreshAll}>Refresh</Button>
            {job.startedAt && <Button size="small" startIcon={<MdRateReview />} onClick={() => onGoReview(jobId)}>Review</Button>}
            {job.status !== 'planning' && (
              <Button size="small" startIcon={<MdFileDownload />} onClick={doExport} disabled={exporting}>Export CSV</Button>
            )}
            {job.startedAt && (
              <Button size="small" startIcon={<MdDownload />} onClick={() => setDownloadOpen(true)}>Download images…</Button>
            )}
            {canReplan && (
              <Tooltip title="Cancel this plan and plan the same IDs again as a new job with today's rules">
                <Button size="small" startIcon={<MdAutorenew />} onClick={() => setReplanOpen(true)}>Re-plan with the current rules</Button>
              </Tooltip>
            )}
            {!['completed', 'cancelled'].includes(job.status) && (
              <Button size="small" color="error" startIcon={<MdCancel />} onClick={() => setCancelOpen(true)}>Cancel job</Button>
            )}
            <Button size="small" variant="outlined" startIcon={<MdAdd />} onClick={onNewJob}>New job</Button>
          </Stack>
        </Stack>
      </Paper>

      {jobError && <Alert severity="error" sx={{ mb: 2 }}>{jobError}</Alert>}

      {job.status === 'planning' && (
        <Paper variant="outlined" sx={{ p: 3, borderRadius: 2, mb: 2 }}>
          <Typography sx={{ fontWeight: 600 }}>Resolving {fmtInt(job.inputCount)} ID(s)...</Typography>
          <Typography variant="body2" color="text.secondary" sx={{ mb: 1.5 }}>
            Finding sizes and silos, sorting styles and choosing rooms. Twenty WebIDs usually take under a minute.
          </Typography>
          <LinearProgress sx={{ '& .MuiLinearProgress-bar': { bgcolor: AI_COLOR } }} />
          {health && health.worker && !health.worker.online && (
            <Alert severity="warning" sx={{ mt: 2 }}>The worker is offline, so planning starts when it is back.</Alert>
          )}
        </Paper>
      )}

      {job.status === 'plan_failed' && (
        <Alert
          severity="error" sx={{ mb: 2 }}
          action={canReplan
            ? <Button color="inherit" size="small" onClick={() => setReplanOpen(true)}>Re-plan with the current rules</Button>
            : <Button color="inherit" size="small" onClick={onNewJob}>New job</Button>}
        >
          Planning failed: {job.planError || 'unknown error'}. {canReplan ? 'Re-plan to try again with the same IDs.' : 'To retry, create a new job with the same IDs.'}
        </Alert>
      )}

      {rejected.length > 0 && job.status === 'planning' && (
        <Alert severity="warning" sx={{ mb: 2 }}>
          {rejected.length} input value(s) were rejected because they do not look like IDs.
          <Button size="small" color="inherit" onClick={() => setListDialog('rejected')} sx={{ ml: 1 }}>Show</Button>
        </Alert>
      )}

      {job.status !== 'planning' && job.status !== 'plan_failed' && (
        <>
          {oldPlan && (
            <Alert
              severity="warning" sx={{ mb: 2 }}
              action={canReplan ? <Button color="inherit" size="small" onClick={() => setReplanOpen(true)}>Re-plan with the current rules</Button> : undefined}
            >
              {OLD_PLAN_TEXT}{replanEligible && !replanReady ? ` ${REPLAN_NOT_READY_TEXT}` : ''}
            </Alert>
          )}

          {/* IDs that made nothing (spec 4.8) */}
          {notMadeOutline.length > 0 && (
            <Alert
              severity="info" sx={{ mb: 2 }}
              action={notMade.length > 10 ? <Button color="inherit" size="small" onClick={() => setListDialog('notMade')}>Show all</Button> : undefined}
            >
              {fmtInt(notMadeOutline.length)} of your IDs {notMadeOutline.length === 1 ? 'is an oval or octagon size' : 'are oval or octagon sizes'}, so nothing was planned for {notMadeOutline.length === 1 ? 'it' : 'them'}:
              {' '}{listIds(notMadeOutline.map((x) => x.input), 10)}. Oval and octagon shots are not made: no oval room exists yet.
              {notMadeCount > notMade.length ? ` (${fmtInt(notMadeCount)} IDs made nothing in all; the first ${fmtInt(notMade.length)} are listed.)` : ''}
            </Alert>
          )}
          {notMadeUnknown.length > 0 && (
            <Alert
              severity="info" sx={{ mb: 2 }}
              action={notMade.length > 10 && !notMadeOutline.length ? <Button color="inherit" size="small" onClick={() => setListDialog('notMade')}>Show all</Button> : undefined}
            >
              {fmtInt(notMadeUnknown.length)} of your IDs {notMadeUnknown.length === 1 ? 'has' : 'have'} a catalog shape this tool does not make:
              {' '}{listIds(notMadeUnknown.map((x) => `${x.input}${x.shape ? ` (${x.shape})` : ''}`), 10)}.
            </Alert>
          )}

          {/* Summary chips */}
          <Stack direction="row" spacing={1} sx={{ flexWrap: 'wrap', rowGap: 1, mb: 2 }}>
            {summaryChips.filter((c) => !(c.hideZero && !c.n)).map((c) => (
              <Chip
                key={c.key}
                label={`${c.label} ${fmtInt(c.n || 0)}`}
                color={c.n ? (c.color || 'default') : 'default'}
                variant={(c.filter && planStatus === c.filter) ? 'filled' : 'outlined'}
                onClick={c.filter ? () => setFilter(c.filter) : (c.dialog && c.n ? () => setListDialog(c.dialog) : undefined)}
              />
            ))}
          </Stack>

          {/* Estimate card */}
          <Paper
            variant="outlined"
            sx={{ p: 2, borderRadius: 2, mb: 2, borderColor: `${AI_COLOR}55`, bgcolor: `${AI_COLOR}08` }}
          >
            <Typography variant="h6" sx={{ fontWeight: 700 }}>
              {bothTotalsText(est.readyCount, speeds, job)}
            </Typography>
            <Typography variant="body2" color="text.secondary">
              {job.model || config.model} · {job.imageSize || config.imageSize} · {job.aspectRatio || config.aspectRatio} · {speedLabel(chosenSpeed)}
              {' '}({fmtInt(est.readyCount)} x {fmtRate(chosenRate)} = {fmtUsd(chosenUsd)})
            </Typography>
            <Typography variant="caption" color="text.secondary" display="block">
              Style lookups already spent {fmtUsd(est.embeddingUsdSpent || 0, 4)}
              {est.embeddingCount !== undefined && est.embeddingCount !== null ? ` on ${fmtInt(est.embeddingCount)} silo embedding(s)` : ' on silo embeddings'}.
              {job.actualUsd ? ` Generated so far: ${fmtUsd(job.actualUsd)}.` : ''}
              {job.possibleUsd ? ` Possibly billed (unknown outcomes): ${fmtUsd(job.possibleUsd)}.` : ''}
            </Typography>
            {(outlineSkippedText || otherSkippedText || summary.discontinuedItems) && (
              <Typography variant="caption" color="text.secondary" display="block">
                {outlineSkippedText ? `Oval or octagon sizes in these designs (never made): ${outlineSkippedText}.` : ''}
                {labelConflictsCount ? (
                  <Tooltip title={Object.entries(labelConflicts).map(([k, v]) => `${k}: ${v}`).join(', ')}>
                    <span>{` ${fmtInt(labelConflictsCount)} of them ${labelConflictsCount === 1 ? 'is' : 'are'} labelled as another shape in the catalog.`}</span>
                  </Tooltip>
                ) : ''}
                {otherSkippedText ? ` Other catalog shapes not made: ${otherSkippedText}.` : ''}
                {summary.discontinuedItems ? ` ${fmtInt(summary.discontinuedItems)} discontinued size(s) ignored.` : ''}
              </Typography>
            )}
            {typed && Number(typed.inputs) > 0 && (
              <Typography variant="caption" color="text.secondary" display="block">
                {fmtInt(typed.inputs)} ItemID{Number(typed.inputs) === 1 ? '' : 's'} typed: each makes only its own shape
                {Number(typed.otherSize) ? `; ${fmtInt(typed.otherSize)} use${Number(typed.otherSize) === 1 ? 's' : ''} another size of that shape (no usable photo of the typed size)` : ''}.
              </Typography>
            )}
            {sceneText && <Typography variant="caption" color="text.secondary" display="block">{sceneText}</Typography>}
            {skipCheck && skipCheck.source === 'snapshot' && (
              <Typography variant="caption" color="text.secondary" display="block">
                Existing room photos were checked against last night&apos;s image list (the live check is not available).
              </Typography>
            )}
            {skipCheck && skipCheck.source !== 'snapshot' && Number(skipCheck.liveFailed) > 0 && (
              <Typography variant="caption" color="text.secondary" display="block">
                Existing room photos of {fmtInt(skipCheck.liveFailed)} of {fmtInt(Number(skipCheck.liveFailed) + (Number(skipCheck.liveWebIds) || 0))} WebIDs
                {' '}were checked against last night&apos;s image list only ({skipCheck.liveError === 'budget' ? 'the live check ran out of time' : 'the live check failed for them'}).
              </Typography>
            )}
            {byRuleText && (
              <Typography variant="caption" color="text.secondary" display="block">Room types: {byRuleText}.</Typography>
            )}
            {gapsByReason.length > 0 && (
              <Typography variant="caption" color="error.main" display="block">
                Gaps: {gapsByReason.slice(0, 3).map(([reason, n]) => `${fmtInt(n)} × ${String(reason).replace(/\.$/, '')}`).join('; ')}
                {gapsByReason.length > 3 ? `; and ${fmtInt(gapsByReason.length - 3)} more reason${gapsByReason.length - 3 === 1 ? '' : 's'} (filter by Gap)` : ''}.
              </Typography>
            )}
          </Paper>

          {planChanged && <Alert severity="warning" sx={{ mb: 2 }} onClose={() => setPlanChanged('')}>{planChanged}</Alert>}
          {batchBlocked && <Alert severity="warning" sx={{ mb: 2 }} onClose={() => setBatchBlocked('')}>{batchBlocked}</Alert>}
          {limitMsg && <Alert severity="error" sx={{ mb: 2 }} onClose={() => setLimitMsg('')}>{limitMsg}</Alert>}
          {speedBlocked && !batchBlocked && job.canStart && <Alert severity="warning" sx={{ mb: 2 }}>{speedBlockedText}</Alert>}

          <BatchesSection batches={jobBatches} now={now} />

          {/* Filters */}
          <Stack direction={{ xs: 'column', sm: 'row' }} spacing={1.5} sx={{ mb: 1.5 }}>
            <TextField
              select size="small" label="Status" value={planStatus} sx={{ minWidth: 170 }}
              onChange={(e) => { setPage(0); setPlanStatus(e.target.value); }}
            >
              <MenuItem value="">All</MenuItem>
              {Object.entries(PLAN_STATUS).map(([k, v]) => <MenuItem key={k} value={k}>{v.label}</MenuItem>)}
              <MenuItem value="existing_elsewhere">In another job</MenuItem>
            </TextField>
            <TextField
              select size="small" label="Style" value={styleFilter} sx={{ minWidth: 170 }}
              onChange={(e) => { setPage(0); setStyleFilter(e.target.value); }}
            >
              <MenuItem value="">All</MenuItem>
              {/* Every style /config lists, including ones added on the Rooms tab. */}
              {styles.map((s) => <MenuItem key={s.code} value={s.code}>{s.name}</MenuItem>)}
            </TextField>
            <TextField
              select size="small" label="Shape" value={shapeFilter} sx={{ minWidth: 190 }}
              onChange={(e) => { setPage(0); setShapeFilter(e.target.value); }}
            >
              <MenuItem value="">All</MenuItem>
              {config.shapes.map((s) => <MenuItem key={s.code} value={s.code}>{s.name}</MenuItem>)}
            </TextField>
            <TextField
              size="small" label="WebID / ItemID" value={qInput} onChange={(e) => setQInput(e.target.value)}
              sx={{ minWidth: 180 }}
            />
            {targetsLoading && <CircularProgress size={20} sx={{ alignSelf: 'center' }} />}
          </Stack>
          {targetsError && <Alert severity="error" sx={{ mb: 1.5 }}>{targetsError}</Alert>}

          {/* Plan table */}
          <TableContainer component={Paper} variant="outlined" sx={{ borderRadius: 2 }}>
            <Table size="small">
              <TableHead>
                <TableRow>
                  <TableCell>Shape</TableCell>
                  <TableCell>ItemID</TableCell>
                  <TableCell>Silo</TableCell>
                  <TableCell>Style</TableCell>
                  <TableCell>Room</TableCell>
                  <TableCell>Status</TableCell>
                  <TableCell align="center">Exclude</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {rows.length === 0 && (
                  <TableRow>
                    <TableCell colSpan={7} sx={{ py: 4, textAlign: 'center', color: 'text.secondary' }}>
                      {targetsLoading ? 'Loading...' : 'No targets match these filters.'}
                    </TableCell>
                  </TableRow>
                )}
                {rows.map((r) => {
                  if (r.group) {
                    return (
                      <TableRow key={r.key} sx={{ bgcolor: theme.palette.action.hover }}>
                        <TableCell colSpan={7} sx={{ py: 0.75 }}>
                          <Typography variant="subtitle2" component="span" sx={{ fontWeight: 700, fontFamily: 'monospace' }}>{r.webId}</Typography>
                          {r.inputId && r.inputId !== r.webId && (
                            <Typography variant="caption" color="text.secondary" sx={{ ml: 1 }}>from input {r.inputId}</Typography>
                          )}
                          {r.design && <Typography variant="caption" color="text.secondary" sx={{ ml: 1 }}>design {r.design}</Typography>}
                        </TableCell>
                      </TableRow>
                    );
                  }
                  const { t } = r;
                  const owned = t.disposition === 'owned';
                  const notGenerated = !t.latestGen;
                  const busy = busyTarget === t.targetId;
                  // A sunroom shot (twin) always has the Sunroom style: never restyled (409 twin_style_fixed).
                  const twin = t.scene === 'sun';
                  const canStyle = canEditJob && !busy && !twin;
                  const canRoom = canEditJob && owned && notGenerated && !busy && t.planStatus !== 'replanning';
                  const canExclude = canEditJob && owned && notGenerated && !busy && t.planStatus !== 'replanning'
                    && ['planned', 'held', 'gap', 'skipped_existing', 'excluded'].includes(t.planStatus);
                  const st = t.style || {};
                  const silo = t.silo || {};
                  const room = t.room || {};
                  const roomStyle = room.code ? room.code.slice(0, 2) : null;
                  const letter = room.letter || roomLetterOf(room.code);
                  return (
                    <TableRow key={r.key} hover sx={{ opacity: t.planStatus === 'excluded' ? 0.55 : 1 }}>
                      <TableCell>
                        <Tooltip title={shapeName(t.shapeCode, config.shapes)}>
                          <Chip size="small" label={t.shapeCode} variant="outlined" sx={{ fontFamily: 'monospace', fontWeight: 700 }} />
                        </Tooltip>
                        {twin && <Box sx={{ mt: 0.5 }}><SunroomShotChip /></Box>}
                      </TableCell>
                      <TableCell>
                        <Typography variant="body2" sx={{ fontWeight: 600, fontFamily: 'monospace' }}>{t.itemId || '-'}</Typography>
                        {t.sizeLabel && <Typography variant="caption" color="text.secondary">{t.sizeLabel}</Typography>}
                      </TableCell>
                      <TableCell>
                        <Stack direction="row" spacing={1} alignItems="center">
                          <Tile
                            src={silo.thumbUrl || silo.smallUrl} alt={t.itemId} size={52}
                            onClick={silo.originalUrl || silo.smallUrl ? () => setLightbox({
                              title: `${t.itemId || t.webId} · ${t.shapeCode}`,
                              initialKey: 'silo',
                              items: [
                                { key: 'silo', label: 'Silo', src: silo.originalUrl || silo.smallUrl, downloadUrl: silo.originalUrl, downloadLabel: 'Open original' },
                                room.baseSmallUrl ? { key: 'base', label: 'Base room', src: room.baseSmallUrl } : null,
                              ],
                            }) : undefined}
                          />
                          <SiloKindBadge kind={silo.kind} />
                        </Stack>
                      </TableCell>
                      <TableCell>
                        {st.code || st.name ? (
                          <StyleChip
                            code={st.code} name={st.name} styles={styles}
                            title={twin
                              ? 'The sunroom shot always uses the Sunroom style.'
                              : (st.runnerUp ? `Runner-up: ${st.runnerUp}${canStyle ? ' · click to change' : ''}` : (canStyle ? 'Click to change the style' : undefined))}
                            onClick={canStyle ? (e) => { setStyleNote(''); setStyleMenu({ anchor: e.currentTarget, target: t }); } : undefined}
                          />
                        ) : (
                          <Chip
                            size="small" label="No style" variant="outlined"
                            onClick={canStyle ? (e) => { setStyleNote(''); setStyleMenu({ anchor: e.currentTarget, target: t }); } : undefined}
                          />
                        )}
                        <Box sx={{ display: 'flex', alignItems: 'center', mt: 0.25 }}>
                          <Typography variant="caption" color="text.secondary">{methodWords(st.method, st.confidence)}</Typography>
                          <FlagIcons flags={st.flags} />
                        </Box>
                      </TableCell>
                      <TableCell>
                        {room.code ? (
                          <Stack direction="row" spacing={1} alignItems="center">
                            <Tile
                              src={room.baseSmallUrl} alt={room.code} size={52}
                              onClick={canRoom ? () => openRoomDialog(t) : (room.baseSmallUrl ? () => setLightbox({
                                title: room.code, initialKey: 'base', items: [{ key: 'base', label: 'Base room', src: room.baseSmallUrl }],
                              }) : undefined)}
                              sx={canRoom ? { cursor: 'pointer' } : undefined}
                            />
                            <Box>
                              <Typography
                                variant="body2"
                                sx={{ fontFamily: 'monospace', fontWeight: 600, cursor: canRoom ? 'pointer' : 'default', '&:hover': canRoom ? { textDecoration: 'underline' } : {} }}
                                onClick={canRoom ? () => openRoomDialog(t) : undefined}
                              >
                                {room.code}
                              </Typography>
                              {letter && (
                                <Typography variant="caption" color="text.secondary" display="block">
                                  {room.letterName || letterName(letter, config.roomLetters)}
                                </Typography>
                              )}
                              <Typography variant="caption" color="text.secondary">
                                {room.method || ''}
                                {roomStyle && st.code && roomStyle !== st.code ? ' · other style' : ''}
                              </Typography>
                            </Box>
                          </Stack>
                        ) : canRoom ? (
                          <Button size="small" variant="outlined" onClick={() => openRoomDialog(t)}>Pick room</Button>
                        ) : <Typography variant="caption" color="text.disabled">no room</Typography>}
                        {t.roomRule && (
                          <Tooltip title={t.roomLetters ? `Room type: ${describeLetters(t.roomLetters, config.roomLetters)}` : 'No room type rule'}>
                            <Typography variant="caption" color="text.secondary" display="block" sx={{ mt: 0.25 }}>
                              {RULE_LABELS[t.roomRule] || t.roomRule}
                            </Typography>
                          </Tooltip>
                        )}
                      </TableCell>
                      <TableCell sx={{ maxWidth: 260 }}>
                        {owned ? (
                          <StatusChip map={PLAN_STATUS} status={t.planStatus} title={t.planReason || undefined} />
                        ) : (
                          <Tooltip title="This WebID x shape belongs to another job and will not be generated again here.">
                            <Chip size="small" color="secondary" variant="outlined" label={`In job #${(t.existingElsewhere && t.existingElsewhere.jobId) || t.ownerJobId || '?'}`} />
                          </Tooltip>
                        )}
                        {!owned && t.existingElsewhere && (
                          <Typography variant="caption" color="text.secondary" display="block">
                            {JOB_STATUS[t.existingElsewhere.jobStatus] ? JOB_STATUS[t.existingElsewhere.jobStatus].label : t.existingElsewhere.jobStatus}
                            {t.existingElsewhere.latestGenStatus ? ` · ${(GEN_STATUS[t.existingElsewhere.latestGenStatus] || {}).label || t.existingElsewhere.latestGenStatus}` : ''}
                            {t.existingElsewhere.reviewState && t.existingElsewhere.latestGenStatus === 'succeeded'
                              ? ` · ${(REVIEW_STATE[t.existingElsewhere.reviewState] || {}).label || t.existingElsewhere.reviewState}` : ''}
                          </Typography>
                        )}
                        {owned && t.planReason && (
                          <Typography variant="caption" color="text.secondary" display="block" sx={{ overflow: 'hidden', textOverflow: 'ellipsis' }}>
                            {t.planReason}
                          </Typography>
                        )}
                        {t.latestGen && (
                          <Stack direction="row" spacing={0.5} sx={{ mt: 0.5 }}>
                            <StatusChip map={GEN_STATUS} status={t.latestGen.status} />
                            {t.latestGen.status === 'succeeded' && <StatusChip map={REVIEW_STATE} status={t.latestGen.reviewState} />}
                            {t.latestGen.attempt > 1 && <Chip size="small" variant="outlined" label={`try ${t.latestGen.attempt}`} />}
                          </Stack>
                        )}
                      </TableCell>
                      <TableCell align="center">
                        {busy ? <CircularProgress size={18} /> : (
                          <Checkbox
                            size="small" checked={t.planStatus === 'excluded'} disabled={!canExclude}
                            onChange={() => toggleExclude(t)}
                            inputProps={{ 'aria-label': `Exclude ${t.webId} ${t.shapeCode}` }}
                          />
                        )}
                      </TableCell>
                    </TableRow>
                  );
                })}
              </TableBody>
            </Table>
          </TableContainer>
          <TablePagination
            component="div" count={targets.total} page={page} onPageChange={(e, p) => setPage(p)}
            rowsPerPage={PAGE_SIZE} rowsPerPageOptions={[PAGE_SIZE]}
          />

          {/* Start footer */}
          <Paper
            elevation={3}
            sx={{
              position: 'sticky', bottom: 8, mt: 2, p: 2, borderRadius: 2, zIndex: 2,
              display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 2, flexWrap: 'wrap',
              borderTop: `3px solid ${AI_COLOR}`,
            }}
          >
            <Box sx={{ minWidth: 0 }}>
              <Typography sx={{ fontWeight: 700 }}>
                {fmtInt(est.readyCount)} image{est.readyCount === 1 ? '' : 's'} ready · about {fmtUsd(chosenUsd)} at {speedLabel(chosenSpeed)}
              </Typography>
              <Typography variant="caption" color="text.secondary">
                {job.canStart
                  ? (isRestart ? 'Queues the targets that became ready since the last Start.' : (half
                    ? 'The images go to Google\'s Batch API at half price; they usually come back within hours, at most 24 h.'
                    : 'Generation begins immediately; results appear as they finish.'))
                  : (job.startBlockers || []).map(blockerText).join(' ') || 'Nothing to start.'}
              </Typography>
            </Box>
            <Box sx={{ flex: '1 1 360px', minWidth: 280, maxWidth: 560 }}>
              <SpeedChooser
                value={chosenSpeed} onChange={setSpeed} speeds={speeds} count={est.readyCount} job={job}
                disabled={!job.canStart || startBusy} dense
              />
            </Box>
            <Tooltip title={job.canStart ? speedBlockedText : (job.startBlockers || []).map(blockerText).join(' ')}>
              <span>
                <Button
                  variant="contained" size="large" startIcon={<MdPlayArrow />}
                  disabled={!job.canStart || startBusy || speedBlocked}
                  onClick={() => {
                    // A plan made before the current rules asks first: re-plan or start anyway (spec 4.11).
                    if (oldPlan) { setOldPlanInfo(null); setOldPlanOpen(true); } else setStartOpen(true);
                  }}
                  sx={{ ...primaryButtonSx, minWidth: 200 }}
                >
                  {isRestart ? 'Generate remaining' : 'Start'}{half ? ' at half price' : ''}
                </Button>
              </span>
            </Tooltip>
          </Paper>
        </>
      )}

      {/* Style menu */}
      <Popover
        open={!!styleMenu} anchorEl={styleMenu ? styleMenu.anchor : null} onClose={() => setStyleMenu(null)}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'left' }}
      >
        {styleMenu && (
          <Box sx={{ p: 1.5, width: 280 }}>
            <Typography variant="subtitle2" sx={{ fontWeight: 700 }}>
              Style for {styleMenu.target.webId} (all sizes)
            </Typography>
            <Typography variant="caption" color="text.secondary" display="block" sx={{ mb: 1 }}>
              Sorter said {styleMenu.target.style && styleMenu.target.style.name ? `${styleMenu.target.style.name} (${methodWords(styleMenu.target.style.method, styleMenu.target.style.confidence)})` : 'nothing'}
              {styleMenu.target.style && styleMenu.target.style.runnerUp ? `, runner-up ${styleMenu.target.style.runnerUp}` : ''}.
              Style belongs to the WebID: your choice applies to every size of {styleMenu.target.webId} that is not
              generated yet, and also teaches the Sorter. Sunroom comes only from the Outdoor &amp; sunroom list.
            </Typography>
            <TextField
              size="small" fullWidth label="Note (optional)" value={styleNote}
              onChange={(e) => setStyleNote(e.target.value.slice(0, 500))} sx={{ mb: 1 }}
            />
            <Stack spacing={0.5}>
              {manualStyles(styles).map((s) => {
                const current = styleMenu.target.style && styleMenu.target.style.code === s.code;
                const c = styleColor(s.code);
                return (
                  <Button
                    key={s.code} size="small" variant={current ? 'contained' : 'outlined'} disabled={current}
                    onClick={() => applyStyle(s.name)}
                    sx={{ justifyContent: 'flex-start', borderColor: `${c}66`, color: current ? undefined : c }}
                  >
                    {s.name}
                  </Button>
                );
              })}
            </Stack>
          </Box>
        )}
      </Popover>

      {/* Room dialog */}
      <Dialog open={!!roomDialog} onClose={roomBusy ? undefined : () => setRoomDialog(null)} maxWidth="md" fullWidth>
        {roomDialog && (
          <>
            <DialogTitle>
              Room for {roomDialog.webId} · {shapeName(roomDialog.shapeCode, config.shapes)}{roomDialog.scene === 'sun' ? ' (sunroom shot)' : ''}
              <Typography variant="body2" color="text.secondary">
                Active {roomDialog.shapeCode} rooms; {styleName(roomDialog.style && roomDialog.style.code, styles) || 'the same style'} first,
                {roomDialog.roomLetters ? ` the usual room type (${describeLetters(roomDialog.roomLetters, config.roomLetters)}) next,` : ''} least used first.
                Click a photo to see it large.
              </Typography>
            </DialogTitle>
            <DialogContent dividers>
              {roomDialog.planStatus === 'skipped_existing' && (
                <Alert severity="info" sx={{ mb: 1.5 }}>
                  This rug already has a room photo on the image server{roomDialog.planReason ? `: ${roomDialog.planReason}` : ''}. Choosing a room makes another one.
                </Alert>
              )}
              <RoomPicker
                shapeCode={roomDialog.shapeCode}
                styleCode={roomDialog.style && roomDialog.style.code}
                value={roomChoice}
                onChange={(code, room) => { setRoomChoice(code); setRoomChoiceObj(room || null); }}
                onPreview={(rm) => setRoomPreview(rm)}
                styles={styles}
                currentRoomCode={roomDialog.room && roomDialog.room.code}
                allowedLetters={roomDialog.roomLetters}
                scene={roomDialog.scene}
                letterNames={config.roomLetters}
              />
              <TextField
                size="small" fullWidth label="Note (optional)" value={roomNote} sx={{ mt: 2 }}
                onChange={(e) => setRoomNote(e.target.value.slice(0, 500))}
              />
            </DialogContent>
            <DialogActions>
              <Button onClick={() => setRoomDialog(null)} disabled={roomBusy}>Cancel</Button>
              <Button
                variant="contained" onClick={applyRoom}
                disabled={roomBusy || !roomChoice || (roomDialog.room && roomDialog.room.code === roomChoice)}
                sx={primaryButtonSx}
              >
                {roomBusy ? <CircularProgress size={20} color="inherit" /> : `Use ${roomChoice || 'room'}`}
              </Button>
            </DialogActions>
          </>
        )}
      </Dialog>

      {/* Unresolved / rejected / not made lists */}
      <Dialog open={!!listDialog} onClose={() => setListDialog(null)} maxWidth="xs" fullWidth>
        <DialogTitle>{{ unresolved: 'Unresolved IDs', notMade: 'IDs that made nothing' }[listDialog] || 'Rejected input'}</DialogTitle>
        <DialogContent dividers sx={{ maxHeight: 420 }}>
          <Typography variant="body2" color="text.secondary" sx={{ mb: 1 }}>
            {{
              unresolved: 'These look like IDs, but the product lookups found no active rug for them.',
              notMade: `Oval and octagon shots are not made: no oval room exists yet.${notMadeCount > notMade.length ? ` The first ${fmtInt(notMade.length)} of ${fmtInt(notMadeCount)} are listed.` : ''}`,
            }[listDialog] || 'These values were not accepted as IDs.'}
          </Typography>
          <List dense>
            {listRows.map((x, i) => (
              <ListItem key={`${x.input}-${i}`} disableGutters>
                <ListItemText
                  primary={<span style={{ fontFamily: 'monospace' }}>{x.input}</span>}
                  secondary={x.reason ? String(x.reason).replace(/_/g, ' ') : null}
                />
              </ListItem>
            ))}
          </List>
        </DialogContent>
        <DialogActions>
          <Button
            onClick={() => {
              const list = listRows.map((x) => x.input);
              if (navigator.clipboard) navigator.clipboard.writeText(list.join('\n')).then(() => notify('Copied', 'success')).catch(() => {});
            }}
          >
            Copy
          </Button>
          <Button onClick={() => setListDialog(null)}>Close</Button>
        </DialogActions>
      </Dialog>

      {/* Start confirm */}
      <ConfirmDialog
        open={startOpen} busy={startBusy} onClose={() => setStartOpen(false)} onConfirm={() => doStart(false)}
        confirmDisabled={speedBlocked}
        title={isRestart ? 'Generate remaining images' : 'Start generation'}
        confirmLabel={half ? `Generate ${fmtInt(est.readyCount)} at half price` : `Generate ${fmtInt(est.readyCount)} now`}
      >
        <Box sx={{ mb: 1.5 }}>
          <SpeedChooser value={chosenSpeed} onChange={setSpeed} speeds={speeds} count={est.readyCount} job={job} disabled={startBusy} />
        </Box>
        {speedBlocked && <Alert severity="warning" sx={{ mb: 1.5 }}>{speedBlockedText}</Alert>}
        {overBudget(config, chosenUsd) && <Alert severity="warning" sx={{ mb: 1.5 }}>{overBudgetText(config, chosenUsd)}</Alert>}
        {half ? (
          <Typography>
            Generate <strong>{fmtInt(est.readyCount)}</strong> image{est.readyCount === 1 ? '' : 's'} at half price for about <strong>{fmtUsd(chosenUsd)}</strong>?
            Google usually returns them within hours, at most 24 h. You can close this page.
          </Typography>
        ) : (
          <Typography>
            Generate <strong>{fmtInt(est.readyCount)}</strong> image{est.readyCount === 1 ? '' : 's'} now for about <strong>{fmtUsd(chosenUsd)}</strong>?
            Results appear as they finish.
          </Typography>
        )}
        <Typography variant="body2" color="text.secondary" sx={{ mt: 1 }}>
          {job.model || config.model} · {job.imageSize || config.imageSize} · {job.aspectRatio || config.aspectRatio} · {speedLabel(chosenSpeed)}. Each image is paid once; nothing that already exists in another job is generated again.
        </Typography>
      </ConfirmDialog>

      {/* Cancel confirm */}
      <ConfirmDialog
        open={cancelOpen} busy={cancelBusy} onClose={() => setCancelOpen(false)} onConfirm={doCancel}
        title={`Cancel job #${job.jobId}?`} confirmLabel="Cancel job" confirmColor="error"
      >
        <Typography variant="body2" sx={{ mb: 1.5 }}>
          Queued generations are cancelled. Images already generating finish and are kept. Half-price batches already at
          Google are cancelled there on a best-effort basis; anything Google already made is still collected and kept.
          Targets that were never generated are released so another job can plan them.
        </Typography>
        <TextField size="small" fullWidth label="Note (optional)" value={cancelNote} onChange={(e) => setCancelNote(e.target.value.slice(0, 500))} />
      </ConfirmDialog>

      {/* Before a room pick: an unusual room (not the usual type / oval base) or a rug that already has a room photo */}
      <ConfirmDialog
        open={!!roomAsk} busy={roomBusy} onClose={() => setRoomAsk(null)}
        onConfirm={() => sendRoom(!!roomAsk && roomAsk.kind === 'existing')}
        title={roomAsk && roomAsk.kind === 'existing' ? 'Make another room shot?' : 'Use this room?'}
        confirmLabel={roomAsk && roomAsk.kind === 'existing' ? 'Make another one' : 'Use it anyway'}
        confirmColor="warning"
      >
        <Typography>{roomAsk ? roomAsk.text : ''}</Typography>
      </ConfirmDialog>

      {/* A plan made before the current rules (spec 4.11): re-plan (recommended) or start anyway */}
      <Dialog open={oldPlanOpen} onClose={startBusy || replanBusy ? undefined : () => setOldPlanOpen(false)} maxWidth="sm" fullWidth>
        <DialogTitle>Start a plan made before the current rules?</DialogTitle>
        <DialogContent dividers>
          <Alert severity="warning" sx={{ mb: 1.5 }}>{OLD_PLAN_START_TEXT}</Alert>
          <Typography variant="body2" sx={{ mb: 1.5 }}>
            Re-plan (recommended) cancels this job and plans the same IDs again as a new job with today&apos;s rules. Start
            anyway queues <strong>{fmtInt(oldPlanInfo && oldPlanInfo.readyCount !== undefined ? oldPlanInfo.readyCount : est.readyCount)}</strong> image
            {(oldPlanInfo && oldPlanInfo.readyCount !== undefined ? oldPlanInfo.readyCount : est.readyCount) === 1 ? '' : 's'} as planned
            {oldPlanInfo && oldPlanInfo.plannedAt ? <> on <NyTime ts={oldPlanInfo.plannedAt} variant="body2" /></> : null}, for about <strong>{fmtUsd(chosenUsd)}</strong> at {speedLabel(chosenSpeed)}.
          </Typography>
          <Box sx={{ mb: 1 }}>
            <SpeedChooser value={chosenSpeed} onChange={setSpeed} speeds={speeds} count={est.readyCount} job={job} disabled={startBusy || replanBusy} />
          </Box>
          {speedBlocked && <Alert severity="warning" sx={{ mt: 1 }}>{speedBlockedText}</Alert>}
          {overBudget(config, chosenUsd) && <Alert severity="warning" sx={{ mt: 1 }}>{overBudgetText(config, chosenUsd)}</Alert>}
          {replanEligible && !replanReady && (
            <Typography variant="caption" color="text.secondary" display="block" sx={{ mt: 1 }}>{REPLAN_NOT_READY_TEXT}</Typography>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setOldPlanOpen(false)} disabled={startBusy || replanBusy}>Cancel</Button>
          <Button color="warning" onClick={() => doStart(true)} disabled={startBusy || replanBusy || speedBlocked || !job.canStart}>
            {startBusy ? <CircularProgress size={20} color="inherit" /> : 'Start anyway'}
          </Button>
          <Button
            variant="contained" sx={primaryButtonSx} disabled={startBusy || replanBusy || !canReplan}
            onClick={() => { setOldPlanOpen(false); setReplanOpen(true); }}
          >
            Re-plan with the current rules
          </Button>
        </DialogActions>
      </Dialog>

      {/* Re-plan with the current rules (spec 4.10) */}
      <ConfirmDialog
        open={replanOpen} busy={replanBusy} onClose={() => setReplanOpen(false)} onConfirm={doReplan}
        title="Re-plan with the current rules" confirmLabel="Re-plan"
      >
        <Typography sx={{ mb: 1.5 }}>
          This cancels job #{job.jobId} and plans the same {fmtInt(job.inputCount)} IDs again as a new job with today&apos;s
          rules (room types, the outdoor &amp; sunroom list, the existing-photo check and ovals). Rooms picked by hand and
          excluded rows on this plan are not kept; style changes are kept.
        </Typography>
        <TextField size="small" fullWidth label="Note (optional)" value={replanNote} onChange={(e) => setReplanNote(e.target.value.slice(0, 500))} />
      </ConfirmDialog>

      <ImageLightbox
        open={!!lightbox} onClose={() => setLightbox(null)}
        items={lightbox ? lightbox.items : []} initialKey={lightbox ? lightbox.initialKey : null}
        title={lightbox ? lightbox.title : ''}
      />
      {/* A base room from the room picker, large */}
      <ImageLightbox
        open={!!roomPreview} onClose={() => setRoomPreview(null)}
        items={roomPreview && roomPreview.base ? [{
          key: 'base', label: 'Base room', src: roomPreview.base.largeUrl || roomPreview.base.originalUrl || roomPreview.base.smallUrl,
          downloadUrl: roomPreview.base.originalUrl, downloadLabel: 'Open original',
        }] : []}
        initialKey="base" title={roomPreview ? roomPreview.roomCode : ''}
      />
      {downloadOpen && (
        <DownloadDialog open onClose={() => setDownloadOpen(false)} jobId={job.jobId} config={config} notify={notify} />
      )}
    </Box>
  );
};

// ===========================================================================
const NewJobTab = ({ config, jobId, onOpenJob, onNewJob, onGoReview, notify, health }) => {
  if (!jobId) return <NewJobForm config={config} onOpenJob={onOpenJob} notify={notify} health={health} />;
  return (
    <PlanView
      key={jobId} jobId={jobId} config={config} notify={notify} health={health}
      onGoReview={onGoReview} onNewJob={onNewJob} onOpenJob={onOpenJob}
    />
  );
};

export default NewJobTab;
